export function createPurchaseOrderPolicy(init: PolicyInit) {
  const status = statusLabelFromId(init.statusId);
  const terminal = status === 'Completed' || status === 'Cancelled';
  const late = status === 'In Progress' || status === 'Received Short';
  const field = (key: FieldKey, row?: RowLike): boolean => {
    if (!key) return false;
    if (terminal) return false;
    if (key.startsWith('Meta.')) {
      if (key === 'Meta.Supplier' || key === 'Meta.Warehouse') return status === 'Created';
      if (key === 'Meta.Comment') return true;
      if (status === 'Shipped' && (key === 'Meta.CurrencyExchangeRate' || key === 'Meta.Message')) return false;
      if (late) return false;
      return true;
    }
    const rowStatus = getRowStatus(row);
    if (rowStatus === 'Cancelled') return key === 'Row.Comment';
    if (rowStatus === 'Completed') return false;
    if (status === 'Received Short') return key === 'Row.Comment';
    if (status === 'In Progress') return key !== 'Row.RTA';
    return true;
  };
  const action = (key: ActionKey, row?: RowLike): boolean => {
    if (terminal) return false;
    if (key === 'Send') return (status === 'Created' || status === 'Changed') && (init.rows ?? []).some((r) => getRowStatus(r) !== 'Cancelled');
    if (key === 'SetConfirmed') return status === 'Sent';
    if (key === 'SetShipped') return status === 'Sent' || status === 'Confirmed';
    if (key === 'SetReceivedShort') return status === 'In Progress' && !init.env.isExternalWarehouse;
    if (key === 'Cancel') return ['Created', 'Changed', 'Sent', 'Confirmed'].includes(status);
    if (key === 'Add_Row') return !late;
    if (key === 'Delete_Row') {
      if (status === 'Received Short' || getRowStatus(row) === 'Cancelled' || getRowStatus(row) === 'Completed') return false;
      const receivingStarted = init.env.hasAnyReceivingStartedForRow?.(row?.PurchaseOrderRowId ?? 0) ?? Number(row?.QuantityReceived) > 0;
      return status !== 'In Progress' || !receivingStarted;
    }
    if (key === 'Edit_Row') return ['Quantity', 'DiscountPct', 'ItemPrice', 'ETA', 'RTA', 'Comment'].some((name) => field(`Row.${name}` as FieldKey, row));
    const rowField = key.replace(/^Edit_/, '').replace(/_Row$/, '');
    return field(`Row.${rowField}` as FieldKey, row);
  };
  const rowEditorReadOnlyReason = (row?: RowLike): string | undefined => {
    if (field('Row.Quantity', row) && field('Row.ItemPrice', row)) return undefined;
    if (terminal) return `This purchase order is ${status}. Its rows are read-only.`;
    const rowStatus = getRowStatus(row);
    if (rowStatus === 'Cancelled') return 'This row is Cancelled. Quantity and price are read-only; its comment can still be edited in the table.';
    if (rowStatus === 'Completed') return 'This row is Completed and cannot be edited.';
    if (status === 'Received Short') return 'This purchase order is Received Short. Quantity and price are read-only; row comments can still be edited in the table.';
    return 'Quantity and price are read-only for this row.';
  };
  return { field, action, status, rowEditorReadOnlyReason };
}

export type OrderStatus = 'Created' | 'Changed' | 'Sent' | 'Confirmed' | 'Shipped' | 'In Progress' | 'Completed' | 'Received Short' | 'Cancelled';
export type RowStatus = 'New' | 'In Progress' | 'Completed' | 'Cancelled';
export type ActionKey =
  | 'Send'
  | 'SetConfirmed'
  | 'SetShipped'
  | 'SetReceivedShort'
  | 'Cancel'
  | 'Add_Row'
  | 'Delete_Row'
  | 'Edit_Row'
  | 'Edit_Quantity_Row'
  | 'Edit_DiscountPct_Row'
  | 'Edit_ItemPrice_Row'
  | 'Edit_ETA_Row'
  | 'Edit_RTA_Row'
  | 'Edit_Comment_Row';
export type MetaFieldKey =
  | 'Supplier'
  | 'Warehouse'
  | 'Message'
  | 'CurrencyExchangeRate'
  | 'ShippingMethod'
  | 'ShippingTerms'
  | 'ShippingTermsDestination'
  | 'SendType'
  | 'SendDestination'
  | 'Comment'
  | 'PaymentMethod'
  | 'PaymentTerm';
export type FieldKey = `Meta.${MetaFieldKey}` | 'Row.Quantity' | 'Row.DiscountPct' | 'Row.ItemPrice' | 'Row.ETA' | 'Row.RTA' | 'Row.Comment';
export interface PolicyEnv {
  isExternalWarehouse: boolean;
  hasAnyReceivingStartedForRow?: (rowId: number) => boolean;
}
export interface PolicyInit {
  statusId?: number | null;
  rows?: RowLike[];
  env: PolicyEnv;
}
type RowLike = {
  PurchaseOrderRowId?: number;
  RowStatus_Value?: string;
  Status_Value?: string;
  Status?: RowStatus | string | { Key?: number; Value?: string };
  QuantityReceived?: number | null;
};
export const STATUS_LIST: ReadonlyArray<{ value: number; label: OrderStatus }> = [
  { value: 28, label: 'Created' },
  { value: 29, label: 'Changed' },
  { value: 30, label: 'Sent' },
  { value: 31, label: 'Confirmed' },
  { value: 32, label: 'Shipped' },
  { value: 33, label: 'In Progress' },
  { value: 34, label: 'Completed' },
  { value: 35, label: 'Received Short' },
  { value: 36, label: 'Cancelled' }
];
export const STATUS_BY_ID = new Map<number, OrderStatus>(STATUS_LIST.map((s) => [s.value, s.label]));
export const STATUS_ID: Readonly<Record<OrderStatus, number>> = Object.freeze(STATUS_LIST.reduce((acc, s) => ({ ...acc, [s.label]: s.value }), {} as Record<OrderStatus, number>));
export function statusLabelFromId(id?: number | null): OrderStatus {
  return id == null ? 'Created' : STATUS_BY_ID.get(Number(id)) ?? 'Cancelled';
}
export function getPurchaseOrderRowStatus(row?: RowLike): string {
  return row?.RowStatus_Value ?? row?.Status_Value ?? (typeof row?.Status === 'object' ? row.Status.Value : row?.Status) ?? 'New';
}
function getRowStatus(row?: RowLike): RowStatus {
  const status = getPurchaseOrderRowStatus(row);
  if (/^in\s*progress$/i.test(status)) return 'In Progress';
  if (/^complete(d)?$/i.test(status)) return 'Completed';
  if (/^cancel(l)?ed$/i.test(status)) return 'Cancelled';
  return 'New';
}
